/**
 * useFormHistory — opt-in undo/redo for forms with HistoryConfig.
 */

import { useRef, useCallback, useReducer, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { HistoryConfig } from '../types/schema';

export interface FormHistoryAPI {
  undo: () => void;
  redo: () => void;
  snapshot: () => void;
  canUndo: boolean;
  canRedo: boolean;
  historyLength: number;
  /** True when restorePrompt is enabled and a saved draft exists in localStorage. */
  hasDraft: boolean;
  restoreDraft: () => void;
  dismissDraft: () => void;
}

export interface UseFormHistoryOptions {
  storageKey?: string;
  schemaId?: string;
  paramsId?: string;
  autoSaveEnabled?: boolean;
  restorePrompt?: boolean;
}

type FormValues = Record<string, unknown>;

type DraftPromptState = { visible: boolean };

type DraftPromptAction = { type: 'DISMISS' } | { type: 'RESTORE' };

function draftPromptReducer(state: DraftPromptState, action: DraftPromptAction): DraftPromptState {
  switch (action.type) {
    case 'DISMISS':
    case 'RESTORE':
      return { visible: false };
    default:
      return state;
  }
}

function detectDraft(options?: UseFormHistoryOptions): boolean {
  if (!options?.restorePrompt || !options.autoSaveEnabled) return false;
  if (!options.storageKey || !options.schemaId) return false;
  try {
    const stored = localStorage.getItem(options.storageKey);
    if (!stored) return false;
    const draft = JSON.parse(stored);
    return Boolean(draft?.values && draft.schemaId === options.schemaId);
  } catch {
    return false;
  }
}

export function useFormHistory(
  form: UseFormReturn<FormValues>,
  config?: HistoryConfig,
  options?: UseFormHistoryOptions,
): FormHistoryAPI {
  const stackRef = useRef<{ past: FormValues[]; future: FormValues[] }>({
    past: [],
    future: [],
  });
  const [draftPrompt, dispatchDraftPrompt] = useReducer(draftPromptReducer, {
    visible: detectDraft(options),
  });

  const maxSnapshots = config?.maxSnapshots ?? 50;

  const hasDraft = draftPrompt.visible;

  const snapshot = useCallback(() => {
    const current = form.getValues();
    const { past } = stackRef.current;

    if (past.length > 0 && isDeepEqual(past[past.length - 1], current)) return;

    past.push(structuredClone(current));
    stackRef.current.future = [];

    if (past.length > maxSnapshots) {
      past.shift();
    }
  }, [form, maxSnapshots]);

  useEffect(() => {
    if (!config?.enabled) return;

    const triggerOn = config.triggerOn ?? 'change';
    if (triggerOn === 'manual') return;

    const debounceMs = config.debounceMs ?? 300;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const subscription = form.watch(() => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        snapshot();
      }, debounceMs);
    });

    return () => {
      subscription.unsubscribe();
      if (timeout) clearTimeout(timeout);
    };
  }, [form, config, snapshot]);

  const undo = useCallback(() => {
    const { past, future } = stackRef.current;
    if (past.length === 0) return;

    const current = form.getValues();
    future.push(structuredClone(current));

    if (past.length > 0 && isDeepEqual(past[past.length - 1], current)) {
      past.pop();
    }

    if (past.length === 0) return;

    const previous = past[past.length - 1];
    form.reset(previous, { keepDirtyValues: false });
    past.pop();
  }, [form]);

  const redo = useCallback(() => {
    const { past, future } = stackRef.current;
    if (future.length === 0) return;

    past.push(structuredClone(form.getValues()));
    const next = future.pop()!;
    form.reset(next, { keepDirtyValues: false });
  }, [form]);

  const restoreDraft = useCallback(() => {
    if (!options?.storageKey || !options.schemaId) return;
    try {
      const stored = localStorage.getItem(options.storageKey);
      if (!stored) return;
      const draft = JSON.parse(stored);
      if (draft?.values && draft.schemaId === options.schemaId) {
        form.reset(draft.values);
        dispatchDraftPrompt({ type: 'RESTORE' });
      }
    } catch {
      /* ignore corrupt storage */
    }
  }, [form, options?.storageKey, options?.schemaId]);

  const dismissDraft = useCallback(() => {
    dispatchDraftPrompt({ type: 'DISMISS' });
    if (!options?.storageKey) return;
    try {
      localStorage.removeItem(options.storageKey);
    } catch {
      /* ignore */
    }
  }, [options?.storageKey]);

  return {
    undo,
    redo,
    snapshot,
    canUndo: stackRef.current.past.length > 0,
    canRedo: stackRef.current.future.length > 0,
    historyLength: stackRef.current.past.length,
    hasDraft,
    restoreDraft,
    dismissDraft,
  };
}

function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === 'object') {
    const aKeys = Object.keys(a as object);
    const bKeys = Object.keys(b as object);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) =>
      isDeepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
      ),
    );
  }

  return false;
}
